import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, ArrowRight, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialBanner() {
  const navigate = useNavigate();
  const [trialInfo, setTrialInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    if (token) {
      fetchTrialStatus();
    } else {
      setLoading(false);
    }
  }, [token]);
  
  const fetchTrialStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/trial/my-trial`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.has_trial) {
          setTrialInfo(data);
        }
      }
    } catch (error) {
      console.error('Error fetching trial status:', error);
    }
    setLoading(false);
  };
  
  if (loading || !trialInfo || dismissed) {
    return null;
  }
  
  const { dias_restantes, plan_nombre, is_expired, is_expiring_soon, status } = trialInfo;
  
  // Trial convertido - no mostrar banner
  if (status === 'convertido') {
    return null;
  }
  
  // Trial expirado - redirigir a página de bloqueo
  if (is_expired) {
    // Redirigir automáticamente a la página de trial expirado
    const currentPath = window.location.pathname;
    const publicPaths = ['/', '/login', '/register', '/eunacom', '/trial-expired', '/pricing', '/cotizar'];
    const isPublicPath = publicPaths.some(path => currentPath === path || currentPath.startsWith('/payment'));
    
    // Solo redirigir si estamos en una ruta de la app (/app/*) y no en rutas públicas
    if (currentPath.startsWith('/app/') && !isPublicPath && currentPath !== '/trial-expired') {
      navigate('/trial-expired', { 
        state: { 
          institutionName: trialInfo.plan_nombre || 'Tu institución',
          planName: trialInfo.plan_nombre,
          expiredDate: trialInfo.fecha_fin,
          daysOverdue: Math.abs(dias_restantes)
        }
      });
      return null;
    }
    
    // Si ya estamos en trial-expired, no mostrar banner
    if (currentPath === '/trial-expired') {
      return null;
    }
    
    // Mostrar banner de bloqueo en páginas públicas
    return (
      <div className="bg-red-600 text-white px-4 py-3" data-testid="trial-expired-banner">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <div>
              <span className="font-semibold">Tu período de prueba ha finalizado.</span>
              <span className="ml-2 text-red-100">
                Contrata ahora para seguir usando SimuClinic.
              </span>
            </div>
          </div>
          <Button 
            size="sm" 
            variant="secondary"
            className="bg-white text-red-600 hover:bg-red-50"
            onClick={() => navigate('/#planes')}
          >
            Contratar ahora
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }
  
  // Trial por expirar (1-3 días)
  if (is_expiring_soon) {
    return (
      <div className="bg-amber-500 text-white px-4 py-3" data-testid="trial-expiring-banner">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 flex-shrink-0 animate-pulse" />
            <div>
              <span className="font-semibold">
                {dias_restantes === 1 ? '¡Último día!' : `Solo ${dias_restantes} días restantes`}
              </span>
              <span className="ml-2 text-amber-100">
                de tu prueba del plan {plan_nombre}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              variant="secondary"
              className="bg-white text-amber-600 hover:bg-amber-50"
              onClick={() => navigate('/#planes')}
            >
              Contratar ahora
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-white hover:bg-amber-600"
              onClick={() => setDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Trial activo normal
  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2" data-testid="trial-active-banner">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 flex-shrink-0" />
          <span>
            <span className="font-medium">Período de prueba:</span>
            <span className="ml-2">{dias_restantes} días restantes del plan {plan_nombre}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="secondary"
            className="bg-white/20 text-white hover:bg-white/30 border-0"
            onClick={() => navigate('/#planes')}
          >
            Ver planes
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => setDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
